/**
 * SDK 로깅 유틸리티
 * debug: true 설정 시에만 콘솔 출력
 */
const REDACTED_VALUE = '[REDACTED]';
const PRIVATE_CONTENT_KEYS = new Set([
  'message',
  'content',
  'contenttext',
  'precontext',
  'transfermemo',
  'submittedvalues',
]);

function isSensitiveKey(key: string): boolean {
  const normalizedKey = key.replace(/[-_]/g, '').toLowerCase();
  return (
    normalizedKey === 'authorization' ||
    normalizedKey.endsWith('token') ||
    PRIVATE_CONTENT_KEYS.has(normalizedKey)
  );
}

function redactEntries(entries: Array<[string, unknown]>, seen: WeakSet<object>): object {
  return Object.fromEntries(
    entries.map(([key, entryValue]) => [
      key,
      isSensitiveKey(key) ? REDACTED_VALUE : redactLogValue(entryValue, seen),
    ])
  );
}

function redactLogValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular]';

  seen.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item) => redactLogValue(item, seen));
    }

    if (value instanceof Date) return value.toISOString();

    if (value instanceof Map) {
      return redactEntries(
        [...value.entries()].map(([key, entryValue]) => [String(key), entryValue]),
        seen
      );
    }

    if (value instanceof Set) {
      return [...value].map((item) => redactLogValue(item, seen));
    }

    if (typeof Headers !== 'undefined' && value instanceof Headers) {
      return redactEntries([...value.entries()], seen);
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        ...(value.cause !== undefined ? { cause: redactLogValue(value.cause, seen) } : {}),
        ...redactEntries(Object.entries(value), seen),
      };
    }

    return redactEntries(Object.entries(value), seen);
  } finally {
    seen.delete(value);
  }
}

export class AICXLogger {
  private enabled: boolean;
  private prefix = '[AICX SDK]';

  constructor(enabled: boolean = false) {
    this.enabled = enabled;
  }

  /** 디버그 모드 설정 */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /** 일반 로그 */
  log(...args: unknown[]): void {
    if (this.enabled) {
      console.log(this.prefix, ...args.map((arg) => redactLogValue(arg)));
    }
  }

  /** 경고 로그 */
  warn(...args: unknown[]): void {
    if (this.enabled) {
      console.warn(this.prefix, ...args.map((arg) => redactLogValue(arg)));
    }
  }

  /** 에러 로그 */
  error(...args: unknown[]): void {
    if (this.enabled) {
      console.error(this.prefix, ...args.map((arg) => redactLogValue(arg)));
    }
  }
}

/** 싱글톤 인스턴스 */
export const logger = new AICXLogger();
